import React, { useEffect, useMemo, useState } from "react";
import { useAppData } from "@/hooks/useAppData";
import { useUserSettingsDispatch } from "@/store/slices/userSettingsDispatch";
import { useAuth } from "@/hooks/useAuth";
import SearchableSelect from "@/lib/shadcn/components/searchable-select";
import { Card, CardContent } from "@/lib/shadcn/ui/card";
import { DatePicker } from "@/lib/shadcn/components/date-picker";
import { getDate, getPreviousYearsDate, useOccupancyActionsDispatch, useOccupancySelector } from "@/store/occupancySlice";
import { Loader2 } from "lucide-react";
import { Alert, AlertDescription } from "@/lib/shadcn/ui/alert";
import YearOverYearChart from "@/pages/graphs/year-over-year-chart";
import { useActiveApartments } from "@/store/slices/apartments";
import { useGetOwnerOccupanciesQuery } from "@/store/booking-management-api";

/**
 * Owner Occupancy Explorer (U2).
 *
 * A fork of the admin `OccupanciesPage` scoped to a single owner's portfolio.
 * The admin page's "Group" card + group selector are dropped entirely; in
 * their place a PORTFOLIO card aggregates every apartment the selected owner
 * owns (owner occupancies query with NO `apartmentId`). The second card is the
 * per-apartment view. Occupancy data flows through the shared
 * `YearOverYearChart` unchanged.
 *
 * Owner selection: derived from `ownerOptions` (the owners the current user can
 * see). The owner selector is rendered ONLY when the user can see more than one
 * owner — a single-owner user has nothing to switch between. Switching owner
 * re-scopes the apartment options to that owner's apartments and resets an
 * out-of-scope apartment selection back to "All" (aggregate).
 */
export const OwnerOccupanciesPage: React.FC = () => {
    const { ownerOptions, selectedOwner } = useAppData();
    const { setSelectedOwner } = useUserSettingsDispatch();
    const { user } = useAuth();
    const activeApartments = useActiveApartments();

    // Whether to offer an owner selector at all. Derived from the user's
    // visible-owner set (available synchronously via AuthContext) rather than
    // the async `/api/owners` options, so the control's presence is stable from
    // the first render — a single-owner user has nothing to switch between.
    const showOwnerSelector = (user?.visibleOwners.length ?? 0) > 1;

    const currentDate = useOccupancySelector(getDate);
    const previousYearsDate = useOccupancySelector(getPreviousYearsDate);
    const { setDate } = useOccupancyActionsDispatch();

    // Owner selection lives in the shared `userSettings` slice (persisted to
    // localStorage), so the choice is remembered across every owner-scoped page —
    // the same source `reports.tsx` / `owner-blt.tsx` read. `0` = unresolved;
    // `selectedApartment` (0 = "All", the owner's whole portfolio) is page-local
    // and resets to 0 whenever it falls out of the selected owner's apartment set.
    const [selectedApartment, setSelectedApartment] = useState<number>(0);

    useEffect(() => {
        if (selectedOwner === 0 && ownerOptions.length > 0) {
            setSelectedOwner(Number(ownerOptions[0].value));
        }
    }, [ownerOptions, selectedOwner, setSelectedOwner]);

    // The apartments the selected owner owns.
    const ownerApartments = useMemo(() => {
        if (selectedOwner === 0) {
            return [];
        }
        return activeApartments.filter((apartment) => apartment.ownerIds.includes(selectedOwner));
    }, [activeApartments, selectedOwner]);

    // Apartment options offered by the per-apartment selector: "All" (the
    // aggregate) plus one entry per owner apartment.
    const apartmentOptions = useMemo(
        () => [
            { value: "0", label: "All" },
            ...ownerApartments.map((apartment) => ({
                value: apartment.id.toString(),
                label: apartment.name,
            })),
        ],
        [ownerApartments],
    );

    // Reset an out-of-scope apartment selection to "All" (EC-003) — e.g. after
    // switching to an owner that does not own the previously-selected apartment.
    useEffect(() => {
        if (selectedApartment !== 0 && !ownerApartments.some((apartment) => apartment.id === selectedApartment)) {
            // eslint-disable-next-line react-hooks/set-state-in-effect
            setSelectedApartment(0);
        }
    }, [ownerApartments, selectedApartment]);

    const selectedApartmentName = useMemo(() => {
        if (selectedApartment === 0) {
            return "All";
        }
        return ownerApartments.find((apartment) => apartment.id === selectedApartment)?.name ?? "All";
    }, [ownerApartments, selectedApartment]);

    useEffect(() => {
        document.title = "Occupancy Explorer";
    }, []);

    const dateStr = currentDate.toISOString().split("T")[0];
    const prevStr = previousYearsDate.toISOString().split("T")[0];
    const skip = selectedOwner === 0;
    const ownerId = selectedOwner;

    const {
        data: occupanciesPortfolio,
        isLoading: isLoadingPortfolio,
        error: errorPortfolio,
    } = useGetOwnerOccupanciesQuery({ ownerId, date: dateStr, apartmentId: 0 }, { skip });

    const { data: occupanciesPortfolioPrev, isLoading: isLoadingPortfolioPrev } = useGetOwnerOccupanciesQuery(
        { ownerId, date: prevStr, apartmentId: 0 },
        { skip },
    );

    const {
        data: occupanciesApartment,
        isLoading: isLoadingApartment,
        error: errorApartment,
    } = useGetOwnerOccupanciesQuery({ ownerId, date: dateStr, apartmentId: selectedApartment }, { skip });

    const { data: occupanciesApartmentPrev, isLoading: isLoadingApartmentPrev } = useGetOwnerOccupanciesQuery(
        { ownerId, date: prevStr, apartmentId: selectedApartment },
        { skip },
    );

    return (
        <div className="space-y-6" data-testid="owner-occupancies-page">
            {/* Filters */}
            <div role="group" aria-label="Filters" className="flex justify-end gap-4">
                {showOwnerSelector && (
                    <div data-testid="owner-occupancies-owner-select">
                        <SearchableSelect
                            variant="volt"
                            selectedValue={selectedOwner !== 0 ? selectedOwner.toString() : ""}
                            onChange={(value) => setSelectedOwner(Number(value))}
                            placeholder="Select Owner"
                            options={ownerOptions}
                            width="w-auto"
                        />
                    </div>
                )}
                <div className="mr-6">
                    <DatePicker variant="volt" selectedValue={currentDate} onChange={setDate} />
                </div>
            </div>

            <Card className="shadow-sm" data-testid="owner-occupancies-portfolio-card">
                <div className="flex justify-between p-6">
                    <h2 className="font-bold">Occupancy Explorer - Portfolio</h2>
                </div>
                {isLoadingPortfolio || isLoadingPortfolioPrev ? (
                    <div role="status" aria-label="Loading portfolio occupancy data" className="flex justify-center items-center min-h-[200px]">
                        <Loader2 className="h-8 w-8 animate-spin" />
                        <span className="sr-only">Loading…</span>
                    </div>
                ) : errorPortfolio ? (
                    <Alert variant="destructive">
                        <AlertDescription>{errorPortfolio.toString()}</AlertDescription>
                    </Alert>
                ) : occupanciesPortfolio !== undefined && occupanciesPortfolio.length === 0 ? (
                    <Alert>
                        <AlertDescription>
                            No data available for the selected date range. Please try a different range.
                        </AlertDescription>
                    </Alert>
                ) : (
                    <CardContent className="p-6">
                        {occupanciesPortfolio !== undefined &&
                            occupanciesPortfolio.length > 0 &&
                            occupanciesPortfolioPrev !== undefined &&
                            occupanciesPortfolioPrev.length > 0 && (
                                <YearOverYearChart
                                    stats={[...occupanciesPortfolio, ...occupanciesPortfolioPrev].map((s) => ({
                                        date: s.date,
                                        value: s.occupancy,
                                    }))}
                                    valueFormatter={(value: number) => value.toFixed(0) + "%"}
                                    startFromMonth={currentDate.getMonth()}
                                />
                            )}
                    </CardContent>
                )}
            </Card>

            <Card className="shadow-sm" data-testid="owner-occupancies-apartment-card">
                <div className="flex justify-between p-6">
                    <h2 className="font-bold">Occupancy Explorer - Apartment: {selectedApartmentName}</h2>
                    <div data-testid="owner-occupancies-apartment-select">
                        <SearchableSelect
                            variant="volt"
                            selectedValue={selectedApartment.toString()}
                            onChange={(value) => setSelectedApartment(Number(value))}
                            placeholder="Select Apartment"
                            options={apartmentOptions}
                            selectedValuePrefix="Apartment"
                            width="w-auto"
                        />
                    </div>
                </div>
                {isLoadingApartment || isLoadingApartmentPrev ? (
                    <div role="status" aria-label="Loading apartment occupancy data" className="flex justify-center items-center min-h-[200px]">
                        <Loader2 className="h-8 w-8 animate-spin" />
                        <span className="sr-only">Loading…</span>
                    </div>
                ) : errorApartment ? (
                    <Alert variant="destructive">
                        <AlertDescription>{errorApartment.toString()}</AlertDescription>
                    </Alert>
                ) : occupanciesApartment !== undefined && occupanciesApartment.length === 0 ? (
                    <Alert>
                        <AlertDescription>
                            No data available for the selected date range. Please try a different range.
                        </AlertDescription>
                    </Alert>
                ) : (
                    <CardContent className="p-6">
                        {occupanciesApartment !== undefined &&
                            occupanciesApartment.length > 0 &&
                            occupanciesApartmentPrev !== undefined &&
                            occupanciesApartmentPrev.length > 0 && (
                                <YearOverYearChart
                                    stats={[...occupanciesApartment, ...occupanciesApartmentPrev].map((s) => ({
                                        date: s.date,
                                        value: s.occupancy,
                                    }))}
                                    valueFormatter={(value: number) => value.toFixed(0) + "%"}
                                    startFromMonth={currentDate.getMonth()}
                                />
                            )}
                    </CardContent>
                )}
            </Card>
        </div>
    );
};

export default OwnerOccupanciesPage;
